import { z } from 'zod';

export const objectIdSchema = z.string().regex(/^[a-f\d]{24}$/i, 'Invalid id');

const emptyToNull = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? null : v);

/** Optional, clearable text. '' and null both mean "no value". */
export const optionalText = (max = 500) => z.preprocess(emptyToNull, z.string().trim().max(max).nullable().optional());

export const optionalEmail = () =>
  z.preprocess(
    emptyToNull,
    z
      .string()
      .trim()
      .toLowerCase()
      .pipe(z.email('Invalid email'))
      .nullable()
      .optional(),
  );

export const requiredEmail = () => z.string().trim().toLowerCase().pipe(z.email('Invalid email'));

export const phoneSchema = z
  .string()
  .trim()
  .regex(/^[+()\d\s-]{6,20}$/, 'Invalid phone number');

export const optionalPhone = () => z.preprocess(emptyToNull, phoneSchema.nullable().optional());

export const optionalObjectId = () => z.preprocess(emptyToNull, objectIdSchema.nullable().optional());

export const optionalDate = () => z.preprocess(emptyToNull, z.coerce.date().nullable().optional());

export const tagsSchema = z.array(z.string().trim().min(1).max(40)).max(20).default([]);

export const addressSchema = z
  .object({
    line1: optionalText(200),
    line2: optionalText(200),
    city: optionalText(100),
    state: optionalText(100),
    postalCode: optionalText(20),
    country: optionalText(100),
  })
  .partial();

export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  sort: z
    .string()
    .regex(/^-?[a-zA-Z_.]+$/)
    .optional(),
  q: z.string().trim().max(100).optional(),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export const passwordSchema = z
  .string()
  .min(10, 'Password must be at least 10 characters')
  .max(128)
  .regex(/[a-zA-Z]/, 'Password must contain a letter')
  .regex(/\d/, 'Password must contain a number');

export const workspaceSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(40)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Use lowercase letters, numbers and hyphens');

/**
 * Build a PATCH schema from a create schema: every field optional and — unlike
 * `.partial()` in zod 4 — WITHOUT applying `.default()` values. Otherwise a
 * PATCH that omits a field would silently reset it to its default.
 */
export function patchOf<S extends z.ZodObject>(schema: S) {
  const shape: Record<string, z.ZodType> = {};
  for (const [key, field] of Object.entries(schema.shape as Record<string, z.ZodType>)) {
    let inner: z.ZodType = field;
    while (inner instanceof z.ZodDefault) inner = inner.unwrap() as z.ZodType;
    shape[key] = inner.optional();
  }
  return z.object(shape) as unknown as z.ZodType<Partial<z.output<S>>, Partial<z.input<S>>>;
}
